import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  increment,
  serverTimestamp
} from 'firebase/firestore';

class PromoCodeService {
  constructor() {
    this.collectionName = 'promoCodes';
  }

  /**
   * 프로모션 코드 생성
   */
  async createPromoCode(codeData) {
    try {
      const { code, type, discount, validUntil, maxUses, planDiscounts, description } = codeData;

      // 코드 중복 체크
      const existing = await this.getPromoCode(code);
      if (existing) {
        throw new Error('이미 존재하는 프로모션 코드입니다.');
      }

      const promoCodeRef = doc(db, this.collectionName, code.toUpperCase());
      const promoCodeData = {
        code: code.toUpperCase(),
        type, // 'percentage' or 'fixed'
        discount, // 할인율(%) 또는 할인 금액(원)
        validUntil: validUntil ? Timestamp.fromDate(new Date(validUntil)) : null,
        maxUses: maxUses || null,
        usedCount: 0,
        active: true,
        description: description || '',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      // 플랜별 고정 할인 금액 (선택사항)
      if (planDiscounts) {
        promoCodeData.planDiscounts = planDiscounts;
      }

      await setDoc(promoCodeRef, promoCodeData);
      console.log('✅ 프로모션 코드 생성 완료:', code);
      return { success: true, code: code.toUpperCase() };
    } catch (error) {
      console.error('❌ 프로모션 코드 생성 실패:', error);
      throw error;
    }
  }

  /**
   * 프로모션 코드 조회
   */
  async getPromoCode(code) {
    try {
      const promoCodeRef = doc(db, this.collectionName, code.toUpperCase());
      const promoCodeDoc = await getDoc(promoCodeRef);

      if (!promoCodeDoc.exists()) {
        return null;
      }

      return {
        code: promoCodeDoc.id,
        ...promoCodeDoc.data()
      };
    } catch (error) {
      console.error('❌ 프로모션 코드 조회 실패:', error);
      return null;
    }
  }

  /**
   * 모든 프로모션 코드 조회
   */
  async getAllPromoCodes() {
    try {
      const promoCodesRef = collection(db, this.collectionName);
      const q = query(promoCodesRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);

      const promoCodes = [];
      querySnapshot.forEach((doc) => {
        promoCodes.push({
          code: doc.id,
          ...doc.data()
        });
      });

      return promoCodes;
    } catch (error) {
      console.error('❌ 프로모션 코드 목록 조회 실패:', error);
      return [];
    }
  }

  /**
   * 활성화된 프로모션 코드만 조회
   */
  async getActivePromoCodes() {
    try {
      const promoCodesRef = collection(db, this.collectionName);
      const q = query(
        promoCodesRef,
        where('active', '==', true),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);

      const promoCodes = [];
      const now = new Date();

      querySnapshot.forEach((doc) => {
        const data = doc.data();

        // 만료일 체크
        if (data.validUntil) {
          const validDate = data.validUntil.toDate();
          if (now > validDate) {
            return; // 만료된 코드는 제외
          }
        }

        // 사용 횟수 체크
        if (data.maxUses && data.usedCount >= data.maxUses) {
          return; // 사용 횟수 초과 코드는 제외
        }

        promoCodes.push({
          code: doc.id,
          ...data
        });
      });

      return promoCodes;
    } catch (error) {
      console.error('❌ 활성 프로모션 코드 조회 실패:', error);
      return [];
    }
  }

  /**
   * 프로모션 코드 유효성 검증
   */
  async validatePromoCode(code) {
    try {
      const promoCode = await this.getPromoCode(code);

      if (!promoCode) {
        return { valid: false, message: '유효하지 않은 프로모션 코드입니다.' };
      }

      if (!promoCode.active) {
        return { valid: false, message: '비활성화된 프로모션 코드입니다.' };
      }

      // 만료일 체크
      if (promoCode.validUntil) {
        const now = new Date();
        const validDate = promoCode.validUntil.toDate();
        if (now > validDate) {
          return { valid: false, message: '만료된 프로모션 코드입니다.' };
        }
      }

      // 사용 횟수 체크
      if (promoCode.maxUses && promoCode.usedCount >= promoCode.maxUses) {
        return { valid: false, message: '사용 가능 횟수를 초과한 프로모션 코드입니다.' };
      }

      return {
        valid: true,
        promoCode,
        message: '유효한 프로모션 코드입니다.'
      };
    } catch (error) {
      console.error('❌ 프로모션 코드 검증 실패:', error);
      return { valid: false, message: '프로모션 코드 검증 중 오류가 발생했습니다.' };
    }
  }

  /**
   * 프로모션 코드 사용 (사용 횟수 증가)
   */
  async usePromoCode(code) {
    try {
      const validation = await this.validatePromoCode(code);
      if (!validation.valid) {
        return validation;
      }

      const promoCodeRef = doc(db, this.collectionName, code.toUpperCase());
      await updateDoc(promoCodeRef, {
        usedCount: increment(1),
        updatedAt: Timestamp.now()
      });

      console.log('✅ 프로모션 코드 사용 완료:', code);
      return { success: true, promoCode: validation.promoCode };
    } catch (error) {
      console.error('❌ 프로모션 코드 사용 실패:', error);
      throw error;
    }
  }

  /**
   * 프로모션 코드 수정
   */
  async updatePromoCode(code, updates) {
    try {
      const promoCodeRef = doc(db, this.collectionName, code.toUpperCase());

      const updateData = {
        ...updates,
        updatedAt: Timestamp.now()
      };

      // validUntil이 있으면 Timestamp로 변환
      if (updates.validUntil) {
        updateData.validUntil = Timestamp.fromDate(new Date(updates.validUntil));
      }

      await updateDoc(promoCodeRef, updateData);
      console.log('✅ 프로모션 코드 수정 완료:', code);
      return { success: true };
    } catch (error) {
      console.error('❌ 프로모션 코드 수정 실패:', error);
      throw error;
    }
  }

  /**
   * 프로모션 코드 활성화/비활성화
   */
  async togglePromoCode(code, active) {
    try {
      await this.updatePromoCode(code, { active });
      console.log(`✅ 프로모션 코드 ${active ? '활성화' : '비활성화'} 완료:`, code);
      return { success: true };
    } catch (error) {
      console.error('❌ 프로모션 코드 토글 실패:', error);
      throw error;
    }
  }

  /**
   * 프로모션 코드 삭제
   */
  async deletePromoCode(code) {
    try {
      const promoCodeRef = doc(db, this.collectionName, code.toUpperCase());
      await deleteDoc(promoCodeRef);
      console.log('✅ 프로모션 코드 삭제 완료:', code);
      return { success: true };
    } catch (error) {
      console.error('❌ 프로모션 코드 삭제 실패:', error);
      throw error;
    }
  }

  /**
   * 할인 가격 계산
   */
  calculateDiscount(originalPrice, promoCode, planId) {
    if (!promoCode) return originalPrice;

    // 플랜별 고정 할인 금액이 있는 경우
    if (promoCode.planDiscounts && promoCode.planDiscounts[planId]) {
      return originalPrice - promoCode.planDiscounts[planId];
    }

    // 타입에 따른 할인 계산
    if (promoCode.type === 'percentage') {
      return Math.floor(originalPrice * (1 - promoCode.discount / 100));
    } else if (promoCode.type === 'fixed') {
      return Math.max(0, originalPrice - promoCode.discount);
    }

    return originalPrice;
  }

  /**
   * 프로모션 코드 통계 조회
   */
  async getPromoCodeStats() {
    try {
      const promoCodes = await this.getAllPromoCodes();

      const totalCodes = promoCodes.length;
      const activeCodes = promoCodes.filter(p => p.active).length;
      const expiredCodes = promoCodes.filter(p => {
        if (!p.validUntil) return false;
        return new Date() > p.validUntil.toDate();
      }).length;
      const totalUsed = promoCodes.reduce((sum, p) => sum + (p.usedCount || 0), 0);

      // 가장 많이 사용된 코드
      const mostUsedCode = promoCodes.reduce((prev, current) => {
        return (current.usedCount || 0) > (prev.usedCount || 0) ? current : prev;
      }, promoCodes[0]);

      return {
        totalCodes,
        activeCodes,
        expiredCodes,
        inactiveCodes: totalCodes - activeCodes,
        totalUsed,
        mostUsedCode: mostUsedCode ? {
          code: mostUsedCode.code,
          usedCount: mostUsedCode.usedCount || 0
        } : null
      };
    } catch (error) {
      console.error('❌ 프로모션 코드 통계 조회 실패:', error);
      return null;
    }
  }

  /**
   * 플랜 부여 프로모션 코드 생성
   * type: 'plan_grant' - 결제 없이 플랜을 부여하는 특별 코드
   */
  async createPlanGrantCode(codeData) {
    try {
      const { code, planType, downloadsRemaining, durationDays, validUntil, maxUses, description } = codeData;

      // 코드 중복 체크
      const existing = await this.getPromoCode(code);
      if (existing) {
        throw new Error('이미 존재하는 프로모션 코드입니다.');
      }

      const promoCodeRef = doc(db, this.collectionName, code.toUpperCase());
      const promoCodeData = {
        code: code.toUpperCase(),
        type: 'plan_grant', // 플랜 부여 타입
        planType: planType || 'MASTER', // 부여할 플랜 (PREMIUM, MASTER 등)
        downloadsRemaining: downloadsRemaining || 1000, // 다운로드 횟수
        durationDays: durationDays || 365, // 구독 기간 (일)
        validUntil: validUntil ? Timestamp.fromDate(new Date(validUntil)) : null, // 코드 만료일
        maxUses: maxUses || null, // 최대 사용 횟수
        usedCount: 0,
        usedBy: [], // 사용한 사용자 목록
        active: true,
        description: description || '',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      await setDoc(promoCodeRef, promoCodeData);
      console.log('✅ 플랜 부여 프로모션 코드 생성 완료:', code);
      return { success: true, code: code.toUpperCase() };
    } catch (error) {
      console.error('❌ 플랜 부여 프로모션 코드 생성 실패:', error);
      throw error;
    }
  }

  /**
   * 플랜 부여 코드 적용 (사용자에게 플랜 부여)
   */
  async applyPlanGrantCode(code, userId, userEmail) {
    try {
      const validation = await this.validatePromoCode(code);
      if (!validation.valid) {
        return validation;
      }

      const promoCode = validation.promoCode;

      // plan_grant 타입인지 확인
      if (promoCode.type !== 'plan_grant') {
        return { success: false, message: '플랜 부여 코드가 아닙니다. 결제 페이지에서 할인 코드로 사용해주세요.' };
      }

      // 이미 사용한 사용자인지 확인
      if (promoCode.usedBy && promoCode.usedBy.includes(userId)) {
        return { success: false, message: '이미 사용한 프로모션 코드입니다.' };
      }

      // 구독 만료일 계산
      const now = new Date();
      const expiresAt = new Date(now.getTime() + (promoCode.durationDays || 365) * 24 * 60 * 60 * 1000);

      // 사용자 구독 정보 업데이트
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        subscription: {
          planType: promoCode.planType || 'MASTER',
          plan: promoCode.planType || 'MASTER',
          status: 'active',
          downloadsRemaining: promoCode.downloadsRemaining || 1000,
          expiresAt: Timestamp.fromDate(expiresAt),
          startDate: Timestamp.now(),
          promoCode: code.toUpperCase(),
          grantedAt: Timestamp.now(),
          isBonus: false
        }
      });

      // 프로모션 코드 사용 기록 업데이트 (실패해도 플랜 부여는 성공)
      try {
        const promoCodeRef = doc(db, this.collectionName, code.toUpperCase());
        await updateDoc(promoCodeRef, {
          usedCount: increment(1),
          usedBy: [...(promoCode.usedBy || []), userId],
          updatedAt: Timestamp.now()
        });
        console.log('✅ 프로모션 코드 사용 기록 업데이트 완료');
      } catch (updateError) {
        console.warn('⚠️ 프로모션 코드 사용 기록 업데이트 실패 (플랜 부여는 성공):', updateError.message);
      }

      console.log('✅ 플랜 부여 완료:', {
        code: code.toUpperCase(),
        userId,
        planType: promoCode.planType,
        expiresAt: expiresAt.toISOString()
      });

      return {
        success: true,
        message: `${promoCode.planType || 'MASTER'} 플랜이 부여되었습니다! (${promoCode.durationDays || 365}일)`,
        planType: promoCode.planType || 'MASTER',
        downloadsRemaining: promoCode.downloadsRemaining || 1000,
        expiresAt: expiresAt.toISOString()
      };
    } catch (error) {
      console.error('❌ 플랜 부여 실패:', error);
      return { success: false, message: `플랜 부여 중 오류가 발생했습니다: ${error.message}` };
    }
  }

  /**
   * 프로모션 코드가 플랜 부여 타입인지 확인
   */
  isPlanGrantCode(promoCode) {
    return promoCode && promoCode.type === 'plan_grant';
  }
}

// 싱글톤 인스턴스 생성 및 export
const promoCodeService = new PromoCodeService();
export default promoCodeService;
